import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { US_STATES } from "@/lib/constants";
import { UserPlus, Trash2, Pencil, ShieldCheck, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "",
  email: "",
  password: "",
  role: "board_member",
  assigned_state: "none",
  phone: "",
};

export default function UsersAdmin({ users, reload, currentUser }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const startCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const startEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name,
      email: u.email,
      password: "",
      role: u.role,
      assigned_state: u.assigned_state || "none",
      phone: u.phone || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        role: form.role,
        assigned_state: form.assigned_state === "none" ? null : form.assigned_state,
        phone: form.phone.trim() || null,
      };
      if (editing) {
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Member updated");
      } else {
        payload.email = form.email;
        payload.password = form.password;
        await api.post("/users", payload);
        toast.success("Member created");
      }
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete board member ${u.name}? Their contacts will be unassigned.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Member removed");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="users-admin">
      <div className="flex items-center justify-between">
        <p className="text-sm text-stone-600 max-w-2xl">
          Manage the 13-seat board roster. Each board member can be assigned a state — they will only
          be able to claim contacts within that state.
        </p>
        <Button
          onClick={startCreate}
          className="rounded-sm bg-[#B85C38] hover:bg-[#9A4D2E] text-white"
          data-testid="add-user-btn"
        >
          <UserPlus className="h-4 w-4 mr-1.5" /> Add Member
        </Button>
      </div>

      <div className="border border-stone-300 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface)] border-b border-stone-300">
            <tr>
              <Th>Member</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th>Role</Th>
              <Th>Assigned State</Th>
              <Th className="text-right pr-5">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-stone-200 last:border-b-0 hover:bg-stone-50">
                <Td>
                  <div className="flex items-center gap-2.5">
                    <span
                      className="h-7 w-7 rounded-full grid place-items-center text-xs font-bold text-white"
                      style={{ backgroundColor: u.role === "admin" ? "#1C1917" : "#B85C38" }}
                    >
                      {u.name?.[0]?.toUpperCase()}
                    </span>
                    <span className="font-medium">{u.name}</span>
                  </div>
                </Td>
                <Td><span className="font-mono text-xs text-stone-700">{u.email}</span></Td>
                <Td>
                  {u.phone ? (
                    <span className="font-mono text-xs text-stone-700">{u.phone}</span>
                  ) : (
                    <span className="text-xs text-stone-400 font-mono">—</span>
                  )}
                </Td>
                <Td>
                  <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider">
                    {u.role === "admin" ? (
                      <><ShieldCheck className="h-3 w-3 text-[#1C1917]" /> Admin</>
                    ) : (
                      <><UserIcon className="h-3 w-3 text-stone-500" /> Board Member</>
                    )}
                  </span>
                </Td>
                <Td>
                  {u.assigned_state ? (
                    <span className="font-mono text-xs">{u.assigned_state}</span>
                  ) : (
                    <span className="text-xs text-stone-400 font-mono">—</span>
                  )}
                </Td>
                <Td className="text-right pr-5">
                  <div className="flex items-center justify-end gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startEdit(u)}
                      className="h-8 rounded-sm border-stone-300"
                      data-testid={`edit-user-${u.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {u.id !== currentUser.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => remove(u)}
                        className="h-8 rounded-sm border-red-200 text-red-700 hover:bg-red-50"
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editing ? "Edit board member" : "Add board member"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1.5 h-10 rounded-sm border-stone-300"
                data-testid="user-name-input"
              />
            </div>
            {!editing && (
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="mt-1.5 h-10 rounded-sm border-stone-300"
                  data-testid="user-email-input"
                />
              </div>
            )}
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider">
                {editing ? "New password (leave blank to keep)" : "Password"}
              </Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="mt-1.5 h-10 rounded-sm border-stone-300"
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider">
                Phone (for SMS notifications)
              </Label>
              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="+1 555 123 4567"
                className="mt-1.5 h-10 rounded-sm border-stone-300 font-mono"
                data-testid="user-phone-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger className="mt-1.5 h-10 rounded-sm border-stone-300" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="board_member">Board Member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider">Assigned State</Label>
                <Select
                  value={form.assigned_state}
                  onValueChange={(v) => setForm((f) => ({ ...f, assigned_state: v }))}
                >
                  <SelectTrigger className="mt-1.5 h-10 rounded-sm border-stone-300" data-testid="user-state-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">None</SelectItem>
                    {US_STATES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-sm border-stone-300"
              data-testid="user-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={busy || !form.name || (!editing && (!form.email || !form.password))}
              className="rounded-sm bg-[#B85C38] hover:bg-[#9A4D2E] text-white"
              data-testid="user-save-btn"
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`text-left px-5 py-3 text-[10px] font-mono uppercase tracking-[0.2em] text-stone-600 font-semibold ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return <td className={`px-5 py-4 align-middle ${className}`}>{children}</td>;
}
